/**
 * VYARA THEME SYSTEM
 * Drop this file into: Assets/vyara-theme.js
 * 
 * Then add this to layout/theme.liquid, INSIDE <head>, as early as possible
 * (before CSS renders, to avoid flash):
 *
 *   <script src="{{ 'vyara-theme.js' | asset_url }}" defer></script>
 *
 * IMPORTANT: The anti-flash script block below must be INLINE in <head>,
 * not deferred. Add this snippet directly in theme.liquid <head>:
 *
 *   <script>
 *     (function(){
 *       var saved = localStorage.getItem('vyara-theme');
 *       var prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
 *       var theme = saved || (prefersDark ? 'dark' : 'light');
 *       document.documentElement.setAttribute('data-theme', theme);
 *     })();
 *   </script>
 */

(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────
  var STORAGE_KEY = 'vyara-theme';
  var DARK = 'dark';
  var LIGHT = 'light';

  // ─── Detect preferred theme ───────────────────────────────
  function getPreferredTheme() {
    var saved = localStorage.getItem(STORAGE_KEY);
    if (saved === DARK || saved === LIGHT) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? DARK : LIGHT;
  }

  // ─── Apply theme to <html> ────────────────────────────────
  function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
    updateToggleAria(theme);
  }

  // ─── Update ARIA label on toggle button ───────────────────
  function updateToggleAria(theme) {
    var btn = document.getElementById('vyara-theme-toggle');
    if (!btn) return;
    btn.setAttribute(
      'aria-label',
      theme === DARK ? 'Switch to light mode' : 'Switch to dark mode'
    );
    btn.setAttribute('title', theme === DARK ? 'Light mode' : 'Dark mode');
  }

  // ─── Toggle between themes ────────────────────────────────
  function toggleTheme() {
    var current = document.documentElement.getAttribute('data-theme') || DARK;
    applyTheme(current === DARK ? LIGHT : DARK);
  }

  // ─── Build toggle button HTML and inject into navbar ─────
  function injectToggleButton() {
    if (document.getElementById('vyara-theme-toggle')) return;

    var btn = document.createElement('button');
    btn.id = 'vyara-theme-toggle';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Switch theme');

    // Sun icon (shown in dark mode — click to go light)
    // Moon icon (shown in light mode — click to go dark)
    btn.innerHTML = [
      '<svg class="vyara-icon-sun" viewBox="0 0 24 24" aria-hidden="true">',
        '<circle cx="12" cy="12" r="4.5"/>',
        '<line x1="12" y1="2" x2="12" y2="4.5"/>',
        '<line x1="12" y1="19.5" x2="12" y2="22"/>',
        '<line x1="4.22" y1="4.22" x2="5.86" y2="5.86"/>',
        '<line x1="18.14" y1="18.14" x2="19.78" y2="19.78"/>',
        '<line x1="2" y1="12" x2="4.5" y2="12"/>',
        '<line x1="19.5" y1="12" x2="22" y2="12"/>',
        '<line x1="4.22" y1="19.78" x2="5.86" y2="18.14"/>',
        '<line x1="18.14" y1="5.86" x2="19.78" y2="4.22"/>',
      '</svg>',
      '<svg class="vyara-icon-moon" viewBox="0 0 24 24" aria-hidden="true">',
        '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>',
      '</svg>'
    ].join('');

    btn.addEventListener('click', toggleTheme);

    // Try to inject next to cart / account icons in the header
    // Dawn theme targets — adjust if your theme differs
    var targets = [
      '.header__icons',           // Dawn / Debut
      '.site-nav__icons',         // Debut fallback
      '.header__icon-list',       // Supply
      '.header-icons',            // Custom
      'header .icons-container',  // Generic
      '.header__right',           // Generic right cluster
      'header nav'                // Last resort
    ];

    var container = null;
    for (var i = 0; i < targets.length; i++) {
      container = document.querySelector(targets[i]);
      if (container) break;
    }

    if (container) {
      // Insert before the last icon (usually cart) so toggle sits naturally
      var lastChild = container.lastElementChild;
      container.insertBefore(btn, lastChild);
    } else {
      // Absolute fallback: fixed position bottom-right
      btn.style.cssText = [
        'position: fixed',
        'bottom: 24px',
        'right: 24px',
        'z-index: 9999',
        'width: 44px',
        'height: 44px',
        'border-radius: 50%',
        'box-shadow: 0 4px 16px rgba(0,0,0,0.3)'
      ].join(';');
      document.body.appendChild(btn);
    }

    // Sync aria label with current theme
    updateToggleAria(document.documentElement.getAttribute('data-theme') || DARK);
  }

  // ─── Listen for system theme changes ─────────────────────
  function watchSystemTheme() {
    var mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener('change', function (e) {
      // Only auto-switch if user hasn't manually picked a theme
      if (!localStorage.getItem(STORAGE_KEY)) {
        applyTheme(e.matches ? DARK : LIGHT);
      }
    });
  }

  // ─── Init ─────────────────────────────────────────────────
  function init() {
    // Apply theme immediately (may already be set by inline script in <head>)
    var current = document.documentElement.getAttribute('data-theme');
    if (!current) {
      applyTheme(getPreferredTheme());
    } else {
      localStorage.setItem(STORAGE_KEY, current);
    }

    watchSystemTheme();

    // Inject button after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectToggleButton);
    } else {
      injectToggleButton();
    }
  }

  init();

})();